import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransitionContext } from './transitionContext';

export { useRouteTransition } from './transitionContext';

type TransitionProviderProps = {
  children: React.ReactNode;
  fadeDurationMs?: number;
};

export const TransitionProvider: React.FC<TransitionProviderProps> = ({
  children,
  fadeDurationMs = 150,
}) => {
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);

  const startTransition = useCallback(
    async (path: string, customFade?: number) => {
      if (isTransitioning) return;
      const duration = customFade ?? fadeDurationMs;
      setIsTransitioning(true);

      await new Promise((resolve) => setTimeout(resolve, duration));
      navigate(path);
      await new Promise((resolve) =>
        setTimeout(resolve, Math.max(100, Math.floor(duration * 0.8))),
      );
      setIsTransitioning(false);
    },
    [fadeDurationMs, isTransitioning, navigate],
  );

  const value = useMemo(
    () => ({ startTransition, isTransitioning }),
    [startTransition, isTransitioning],
  );

  return (
    <TransitionContext.Provider value={value}>
      {children}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#1c1c1c',
          opacity: isTransitioning ? 1 : 0,
          transition: `opacity ${fadeDurationMs}ms ease`,
          zIndex: 1050,
          pointerEvents: isTransitioning ? 'auto' : 'none',
        }}
      />
    </TransitionContext.Provider>
  );
};

export default TransitionProvider;
